import { useState, useEffect } from 'react';
import StatusCode from 'status-code-enum';

import { SaveSearchPanel } from 'components/PageWrappers/AdDetails/components/SaveSearchPanel/SaveSearchPanel';
import { Link } from 'components/Toolkit/Button/Link';
import { useUserContext } from 'contexts/UserContext';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { savedSearchApi } from 'api/savedSearches';
import { fireToast } from 'helpers/Toasts';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';

interface RevealSaveSearchPanelProps {
  adId: number;
  isSearchSaved: boolean;
  setIsSearchSaved: (t: boolean) => void;
  baseUrl: string;
  className?: string;
}

function RevealSaveSearchPanel({
  adId,
  isSearchSaved,
  setIsSearchSaved,
  baseUrl,
  className,
}: RevealSaveSearchPanelProps) {
  const {
    user,
    isLoginModalOpen,
    setIsSaveSearchModalOpen,
    setIsInstantAlertAvailable,
  } = useUserContext();
  const { handleLogin, accessToken } = useAuthOptions();
  const [queueDisplaySaveSeachModal, setQueueDisplaySaveSeachModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (user?.id && queueDisplaySaveSeachModal) {
      setIsSaveSearchModalOpen(true);
      setQueueDisplaySaveSeachModal(false);
    }
    if (!isLoginModalOpen) {
      setQueueDisplaySaveSeachModal(false);
    }
  }, [isLoginModalOpen]);

  useEffect(() => {
    if (!user?.id) {
      setIsSearchSaved(false);
    }
  }, [user?.id]);

  async function handleUserEvent() {
    if (user?.id) {
      try {
        const { data } = await savedSearchApi.adDetailsPagePreCheck(
          adId,
          user.id,
          formatAuthorizationHeader(accessToken),
        );
        if (data.instantAlert) {
          setIsInstantAlertAvailable(true);
        } else {
          setIsInstantAlertAvailable(false);
        }
        setIsSaveSearchModalOpen(true);
      } catch (error) {
        if (
          error?.response?.status === StatusCode.ClientErrorUnprocessableEntity
        ) {
          fireToast({
            type: 'ERROR',
            text: (
              <>
                You already have a Saved Search for these filters.{' '}
                <Link
                  ofType="SECONDARY"
                  inline
                  href={`${baseUrl}/dashboard/searches`}
                >
                  View all Saved Searches
                </Link>
              </>
            ),
            closeOnClick: false,
          });
        } else
          fireToast({
            type: 'ERROR',
            text: 'Oops! Something went wrong, please try again later',
          });
      }
    } else {
      loginAndQueueSearch();
    }
  }

  function loginAndQueueSearch() {
    setQueueDisplaySaveSeachModal(true);
    handleLogin();
  }

  useOnUpdateOnly(() => {
    setIsSearchSaved(false);
  }, [adId]);

  return (
    <SaveSearchPanel
      className={className}
      handleClick={handleUserEvent}
      isSearchSaved={isSearchSaved}
    />
  );
}

export { RevealSaveSearchPanel };
